// src/products/equityManager/components/CreateTransactionModal.js
import React, { useState, useEffect } from 'react';
import { X, ArrowRightLeft, AlertTriangle, Building2 } from 'lucide-react';
import EquityService from '../../../services/equityService';
import CurrencyService from '../../../services/currencyService';

const CreateTransactionModal = ({ selectedCompany, onClose, onSubmit, addError }) => {
  const [formData, setFormData] = useState({
    transaction_type: 'transfer',
    original_issuance_id: '',
    from_shareholder_id: '',
    to_shareholder_id: '',
    shares: '',
    price_per_share: '',
    transaction_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [availableIssuances, setAvailableIssuances] = useState([]);
  const [shareholders, setShareholders] = useState([]);
  const [selectedIssuance, setSelectedIssuance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [issuancesData, shareholdersData] = await Promise.all([
          EquityService.fetchAvailableIssuances(selectedCompany.id),
          EquityService.fetchShareholders(selectedCompany.id)
        ]);
        
        setAvailableIssuances(issuancesData);
        setShareholders(shareholdersData);
      } catch (error) {
        console.error('Error fetching data:', error);
        addError('Failed to load transaction data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedCompany.id, addError]);

  useEffect(() => {
    // Update form when issuance is selected
    if (selectedIssuance) {
      setFormData(prev => ({
        ...prev,
        from_shareholder_id: selectedIssuance.shareholder_id,
        // For void transactions, always use full issuance shares and price
        shares: prev.transaction_type === 'void' 
          ? selectedIssuance.shares.toString() 
          : (prev.shares || selectedIssuance.shares.toString()),
        price_per_share: prev.transaction_type === 'void'
          ? selectedIssuance.price_per_share.toString()
          : (prev.price_per_share || selectedIssuance.price_per_share.toString())
      }));
    }
  }, [selectedIssuance]);

  useEffect(() => {
    // When transaction type changes to void, update shares and price to full issuance
    if (formData.transaction_type === 'void' && selectedIssuance) {
      setFormData(prev => ({
        ...prev,
        shares: selectedIssuance.shares.toString(),
        price_per_share: selectedIssuance.price_per_share.toString()
      }));
    }
  }, [formData.transaction_type, selectedIssuance]);

  const handleIssuanceChange = (issuanceId) => {
    const issuance = availableIssuances.find(i => i.id === parseInt(issuanceId));
    setSelectedIssuance(issuance);
    setFormData(prev => ({
      ...prev,
      original_issuance_id: issuanceId
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.original_issuance_id) {
      addError('Please select a share issuance');
      return false;
    }
    
    if (!formData.shares || parseInt(formData.shares) <= 0) {
      addError('Please enter a valid number of shares');
      return false;
    }
    
    if (selectedIssuance && parseInt(formData.shares) > selectedIssuance.shares) {
      addError('Cannot transact more shares than available in the issuance');
      return false;
    }
    
    if (formData.transaction_type === 'transfer' && !formData.to_shareholder_id) {
      addError('Please select a receiving shareholder for transfer');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      
      // Prepare transaction data
      const transactionData = {
        ...formData,
        shares: parseInt(formData.shares),
        price_per_share: formData.price_per_share ? parseFloat(formData.price_per_share) : null,
        // For void/buyback, to_shareholder_id should be null (company)
        to_shareholder_id: formData.transaction_type === 'transfer' ? formData.to_shareholder_id : null
      };
      
      await onSubmit(transactionData);
    } catch (error) {
      console.error('Error submitting transaction:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getTransactionTypeInfo = () => {
    switch (formData.transaction_type) {
      case 'transfer':
        return {
          icon: <ArrowRightLeft className="h-5 w-5 text-blue-600" />,
          title: 'Transfer Shares',
          description: 'Move shares from one shareholder to another',
          color: 'blue'
        };
      case 'void':
        return {
          icon: <X className="h-5 w-5 text-red-600" />,
          title: 'Void Shares',
          description: 'Cancel shares due to error or non-payment',
          color: 'red'
        };
      case 'buyback':
        return {
          icon: <Building2 className="h-5 w-5 text-green-600" />,
          title: 'Buyback Shares',
          description: 'Company repurchases shares from shareholder',
          color: 'green'
        };
      default:
        return {
          icon: <AlertTriangle className="h-5 w-5 text-gray-600" />,
          title: 'Transaction',
          description: 'Share transaction',
          color: 'gray'
        };
    }
  };

  const typeInfo = getTransactionTypeInfo();

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading transaction data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            {typeInfo.icon}
            <div className="ml-3">
              <h2 className="text-xl font-semibold text-gray-900">{typeInfo.title}</h2>
              <p className="text-sm text-gray-600">{typeInfo.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Transaction Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transaction Type
            </label>
            <select
              name="transaction_type"
              value={formData.transaction_type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="transfer">Transfer - Move shares between shareholders</option>
              <option value="void">Void - Cancel shares due to error</option>
              <option value="buyback">Buyback - Company repurchases shares</option>
            </select>
          </div>

          {/* Share Issuance Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Share Issuance
            </label>
            <select
              name="original_issuance_id"
              value={formData.original_issuance_id}
              onChange={(e) => handleIssuanceChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Choose an issuance to transact...</option>
              {availableIssuances.map((issuance) => (
                <option key={issuance.id} value={issuance.id}>
                  {issuance.display_name}
                </option>
              ))}
            </select>
            {selectedIssuance && (
              <div className="mt-2 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Available:</span> {selectedIssuance.shares} shares at {CurrencyService.formatAmount(selectedIssuance.price_per_share, selectedCompany?.currency || 'USD')}/share
                </p>
              </div>
            )}
          </div>

          {/* Receiving Shareholder (only for transfers) */}
          {formData.transaction_type === 'transfer' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transfer To
              </label>
              <select
                name="to_shareholder_id"
                value={formData.to_shareholder_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select receiving shareholder...</option>
                {shareholders
                  .filter(s => s.id !== parseInt(formData.from_shareholder_id))
                  .map((shareholder) => (
                    <option key={shareholder.id} value={shareholder.id}>
                      {shareholder.name || `${shareholder.first_name} ${shareholder.last_name}`}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Number of Shares */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Shares
              {formData.transaction_type === 'void' && (
                <span className="text-xs text-gray-500 ml-2">(Entire issuance will be voided)</span>
              )}
            </label>
            <input
              type="number"
              name="shares"
              value={formData.shares}
              onChange={handleInputChange}
              min="1"
              max={selectedIssuance?.shares}
              disabled={formData.transaction_type === 'void'}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formData.transaction_type === 'void' ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              required
            />
            {selectedIssuance && (
              <p className="mt-1 text-sm text-gray-500">
                Maximum: {selectedIssuance.shares} shares available
              </p>
            )}
          </div>

          {/* Price per Share */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price per Share ({CurrencyService.getCurrencySymbol(selectedCompany?.currency || 'USD')}) 
              {formData.transaction_type === 'void' ? (
                <span className="text-xs text-gray-500 ml-2">(Uses issuance price)</span>
              ) : (
                <span className="text-xs text-gray-500 ml-2">(optional)</span>
              )}
            </label>
            <input
              type="number"
              name="price_per_share"
              value={formData.price_per_share}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              disabled={formData.transaction_type === 'void'}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formData.transaction_type === 'void' ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="0.00"
            />
            <p className="mt-1 text-sm text-gray-500">
              Leave blank for nil consideration transactions
            </p>
          </div>

          {/* Transaction Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transaction Date
            </label>
            <input
              type="date"
              name="transaction_date"
              value={formData.transaction_date}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any additional details about this transaction..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`px-4 py-2 rounded-md text-white font-medium ${
                submitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {submitting ? 'Processing...' : `Create ${formData.transaction_type}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTransactionModal;